import { Attr, Calc, Column } from '@mode-switch/model';
import { ApiTypes, DataViewResponseTypes, FlamingoTypes, StateRecord } from '@mode/shared/contract-common';
import { QueryRunViewResource } from './query-run-view.resource';

// Line between types and util is blurry
// eslint-disable-next-line @nx/enforce-module-boundaries
import { Switch } from '@mode/shared/util-switch';

/****************
 *
 * Following helpers are used for transforms related to resources.
 *
 * There are distinct helpers for legacy resource types and new resource types.
 * More details in the explaination at the top of this file.
 *
 ****************/

/**
 * Generate a calc attr from a legacy attr resource.
 *
 * Note - this uses the legacy attr resource type in order not to change types and state
 * in legacy areas of the codebase. (ie - selected-report)
 */
export function makeCalcFromLegacyAttrResource(
  attr: DataViewResponseTypes.QueryRunViewAttrResource
): FlamingoTypes.ModeCalc {
  const { token, is_local: isLocal, color, format, defaults_aggregate: defaultsAggregate } = attr;

  const defaults = Switch.Calcs.defaults(attr.formula_type, defaultsAggregate);
  const base = Calc.make(
    attr.name,
    attr.formula_source,
    attr.type,
    attr.role,
    attr.scale,
    attr.formula_type,
    attr.formula_role,
    {
      desc: attr.desc,
      defaults,
    }
  );

  return {
    ...base,
    token,
    isLocal,
    color,
    format,
    defaultsAggregate,
  };
}

/**
 * Generate a column attr from legacy attr resource
 *
 * Note - this uses the legacy attr resource type in order not to change types and state
 * in legacy areas of the codebase. (ie - selected-report)
 */
export function makeColumnFromLegacyAttrResource(
  attr: DataViewResponseTypes.QueryRunViewAttrResource
): FlamingoTypes.ModeColumn {
  const { token, is_local: isLocal, color, format, defaults_aggregate: defaultsAggregate } = attr;

  const defaults = Switch.Columns.defaults(attr.name, attr.formula_type, defaultsAggregate);
  const base = Column.make(
    attr.name,
    attr.formula_source,
    attr.type,
    attr.role,
    attr.scale,
    attr.formula_type,
    attr.formula_role,
    {
      desc: attr.desc,
      defaults,
    }
  );

  return {
    ...base,
    token,
    isLocal,
    color,
    format,
    defaultsAggregate,
  };
}

/**
 * Make Attrs from the legacy attr resources.
 *
 * Note - this uses the legacy attr resource type in order not to change types and state
 * in legacy areas of the codebase. (ie - selected-report)
 */
export function makeAttrsFromLegacyResources(
  attrResources: DataViewResponseTypes.QueryRunViewAttrResource[]
): FlamingoTypes.ModeAttr[] {
  const attrs: FlamingoTypes.ModeAttr[] = [];

  for (const attrResource of attrResources) {
    switch (attrResource.source) {
      case Attr.Source.Calc: {
        attrs.push(makeCalcFromLegacyAttrResource(attrResource));
        break;
      }
      case Attr.Source.Column: {
        attrs.push(makeColumnFromLegacyAttrResource(attrResource));
        break;
      }
    }
  }

  return attrs;
}

export interface QueryRunResource extends StateRecord, ApiTypes.ModeResource {
  query_token: string;
  source: string;
  raw_source: string;
  limit: boolean;
  rendered_source: string;
  state: string;
  layout: string;
  data_source_id: string;
  data_source_token: string;
  completed_at: string;
  startedAt: string;
  created_at: string;
  error_code?: string;
  error_type?: string;
  error_message: string;
  help_url?: string;
  dbt_metric_id?: number;
}

enum QueryRunResourceErrorTypes {
  largeResultSet = 'result_set_too_large',
  generic = 'generic',
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace QueryRunResourceHelpers {
  export function isQueryResultOversized(resource: QueryRunResource) {
    return resource.hasOwnProperty('error_type') && resource.error_type === QueryRunResourceErrorTypes.largeResultSet;
  }

  export function parseOversizeErrorMessage(resource: QueryRunResource) {
    if (isQueryResultOversized(resource)) {
      // Oversized error app.asana.com/0/663129331545025/787148599250855
      const [errorText, errorResolutionText] = resource.error_message.split('\n');
      return {
        isDbResultOversized: true,
        errorText,
        errorResolutionText,
        resolutionLink: resource.help_url,
      };
    } else {
      return {
        isDbResultOversized: false,
      };
    }
  }

  export function unwrapQueryRunAttrs(queryRunResource: any): DataViewResponseTypes.QueryRunViewAttrResource[] {
    const queryRunViewResource = queryRunResource.embedded('view') as QueryRunViewResource;

    let queryRunViewAttrResources = [];
    if (queryRunViewResource != null) {
      const queryRunViewAttrsCollection = queryRunViewResource.embedded('attrs');

      if (queryRunViewAttrsCollection != null) {
        queryRunViewAttrResources =
          queryRunViewAttrsCollection.embedded('attrs') || ([] as DataViewResponseTypes.QueryRunViewAttrResource[]);
      }
    }

    return queryRunViewAttrResources;
  }

  export function makeView(queryRunResource: any, queryRunResultResource: any): FlamingoTypes.ModeView {
    const { token, query_token } = queryRunResource;
    const queryRunViewAttrResources = unwrapQueryRunAttrs(queryRunResource);
    const attrs = makeAttrsFromLegacyResources(queryRunViewAttrResources);

    // Make this view with columns and overrides
    const { columns } = queryRunResultResource;
    return Switch.Views.make(query_token, token, columns, attrs);
  }

  export function makeCalcAttrs(queryRunResource: any, queryRunResultResource: any): FlamingoTypes.ModeCalc[] {
    const queryRunViewAttrResources = unwrapQueryRunAttrs(queryRunResource);
    const attrs: FlamingoTypes.ModeCalc[] = [];

    for (const attrResource of queryRunViewAttrResources) {
      switch (attrResource.source) {
        case Attr.Source.Calc: {
          attrs.push(makeCalcFromLegacyAttrResource(attrResource));
          break;
        }
      }
    }

    const { columns } = queryRunResultResource;
    return Switch.makeAttrs(columns, attrs).filter(
      (attr): attr is FlamingoTypes.ModeCalc => attr.source === Attr.Source.Calc
    );
  }

  export function makeColumnAttrs(queryRunResource: any, queryRunResultResource: any): FlamingoTypes.ModeColumn[] {
    const queryRunViewAttrResources = unwrapQueryRunAttrs(queryRunResource);
    const attrs: FlamingoTypes.ModeColumn[] = [];

    for (const attrResource of queryRunViewAttrResources) {
      switch (attrResource.source) {
        case Attr.Source.Column: {
          attrs.push(makeColumnFromLegacyAttrResource(attrResource));
          break;
        }
      }
    }

    const { columns } = queryRunResultResource;
    return Switch.makeAttrs(columns, attrs).filter(
      (attr): attr is FlamingoTypes.ModeColumn => attr.source === Attr.Source.Column
    );
  }
}
